<script context="module">
    let memoryCouriersCache = [];
    let hasLoadedCouriersOnce = false;
</script>

<script>
	import { onMount, onDestroy } from "svelte";
	import {
		getApiBase,
		getCurrentStore,
		fetchCouriers,
		createCourier,
		updateCourier,
		deleteCourier,
		toggleCourierStatus,
		returnCourier,
		reorderCouriersQueue,
		fetchCourierSettlement,
		settleCourierAccount,
		fetchCourierSettlementHistory
	} from "../services/api";
	import Icon from "../components/icons/Icon.svelte";
	import CourierModal from "../components/couriers/CourierModal.svelte";
	import CouriersQueueTab from "../components/couriers/CouriersQueueTab.svelte";
	import CouriersTeamTab from "../components/couriers/CouriersTeamTab.svelte";
	import CouriersSettlementTab from "../components/couriers/CouriersSettlementTab.svelte";
	import CouriersHistoryTab from "../components/couriers/CouriersHistoryTab.svelte";

	export let onNotify = (msg, type) => {};

	let activeViewTab = "queue"; // 'queue' | 'team' | 'settlement' | 'history'
	let couriers = memoryCouriersCache.length > 0 ? [...memoryCouriersCache] : [];
	let isLoading = !hasLoadedCouriersOnce && couriers.length === 0;
	let errorMsg = "";

	// Métricas
	let totalCouriers = 0;
	let availableCount = 0;
	let busyCount = 0;
	let totalDeliveriesToday = 0;

	// Modal de Cadastro/Edição
	let showCourierModal = false;
	let isEditing = false;
	let courierForm = {
		id: "",
		name: "",
		phone: "",
		vehicle_type: "moto",
		plate: "",
		fee_type: "fixed",
		default_fee: 5.00,
		daily_rate: 0.00
	};
	let isSaving = false;

	// Acerto Financeiro
	let selectedCourierForSettlement = null;
	let settlementDate = (() => {
		try {
			const parts = new Intl.DateTimeFormat('pt-BR', {
				timeZone: 'America/Sao_Paulo',
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			}).formatToParts(new Date());
			const y = parts.find(p => p.type === 'year')?.value || '2026';
			const m = parts.find(p => p.type === 'month')?.value || '01';
			const d = parts.find(p => p.type === 'day')?.value || '01';
			return `${y}-${m}-${d}`;
		} catch (_) {
			const now = new Date();
			const y = now.getFullYear();
			const m = String(now.getMonth() + 1).padStart(2, '0');
			const d = String(now.getDate()).padStart(2, '0');
			return `${y}-${m}-${d}`;
		}
	})();
	let settlementData = null;
	let isLoadingSettlement = false;
	let settlementNotes = "";
	let isSettling = false;

	// Histórico
	let settlementHistory = [];
	let isLoadingHistory = false;

	// Sincronização em Tempo Real da Fila via WebSocket e Contingência Inteligente
	let socket = null;
	let isSocketConnected = false;
	let refreshTimeout = null;
	let contingencyInterval = null;
	let lastFetchTimestamp = 0;

	function scheduleQueueRefresh() {
		if (refreshTimeout) clearTimeout(refreshTimeout);
		refreshTimeout = setTimeout(() => {
			loadCouriers(false);
		}, 100);
	}

	function startContingencyPolling() {
		if (contingencyInterval) return;
		contingencyInterval = setInterval(() => {
			if (!isSocketConnected) {
				loadCouriers(false);
			}
		}, 20000); // Polling de contingência apenas se desconectado
	}

	function stopContingencyPolling() {
		if (contingencyInterval) {
			clearInterval(contingencyInterval);
			contingencyInterval = null;
		}
	}

	function onWindowFocus() {
		const now = Date.now();
		if (now - lastFetchTimestamp > 30000) {
			loadCouriers(false);
		}
	}

	onMount(async () => {
		await loadCouriers(true);

		if (typeof window !== "undefined") {
			window.addEventListener("courier-queue-refresh", scheduleQueueRefresh);
			window.addEventListener("focus", onWindowFocus);

			if (window.io) {
				try {
					socket = window.io(getApiBase());

					socket.on("connect", () => {
						isSocketConnected = true;
						stopContingencyPolling();
						const store = getCurrentStore();
						const token = localStorage.getItem("matafome_token") || "";
						if (store?.id) {
							socket.emit("join_store", { 
								storeId: store.id,
								accessKey: store.access_key,
								token: token
							});
						}
					});

					socket.on("courier_queue_updated", () => {
						scheduleQueueRefresh();
					});

					socket.on("disconnect", () => {
						isSocketConnected = false;
						startContingencyPolling();
					});

					socket.on("connect_error", () => {
						isSocketConnected = false;
						startContingencyPolling();
					});
				} catch (e) {
					startContingencyPolling();
				}
			} else {
				startContingencyPolling();
			}
		}
	});

	onDestroy(() => {
		if (refreshTimeout) clearTimeout(refreshTimeout);
		stopContingencyPolling();
		if (socket) {
			try { socket.disconnect(); } catch (_) {}
			socket = null;
		}
		if (typeof window !== "undefined") {
			window.removeEventListener("courier-queue-refresh", scheduleQueueRefresh);
			window.removeEventListener("focus", onWindowFocus);
		}
	});

	async function loadCouriers(showSpinner = false) {
		lastFetchTimestamp = Date.now();
		if (showSpinner && couriers.length === 0) {
			isLoading = true;
		}
		errorMsg = "";
		try {
			const data = await fetchCouriers();
			couriers = data || [];
			memoryCouriersCache = couriers;
			hasLoadedCouriersOnce = true;
		} catch (err) {
			errorMsg = err.message || "Falha ao carregar entregadores.";
		} finally {
			isLoading = false;
		}
	}

	// Métricas reativas automáticas (garante atualização instantânea na UI)
	$: totalCouriers = couriers.length;
	$: availableCount = couriers.filter(c => c.status === "available").length;
	$: busyCount = couriers.filter(c => c.status === "busy").length;
	$: totalDeliveriesToday = couriers.reduce((acc, c) => acc + (c.todayMetrics?.deliveries || 0), 0);

	function openCreateModal() {
		isEditing = false;
		courierForm = {
			id: "",
			name: "",
			phone: "",
			vehicle_type: "moto",
			plate: "",
			fee_type: "fixed",
			default_fee: 5.00,
			daily_rate: 0.00
		};
		showCourierModal = true;
	}

	function openEditModal(courier) {
		isEditing = true;
		courierForm = {
			id: courier.id,
			name: courier.name,
			phone: courier.phone || "",
			vehicle_type: courier.vehicle_type || "moto",
			plate: courier.plate || "",
			fee_type: courier.fee_type || "fixed",
			default_fee: courier.default_fee || 5.00,
			daily_rate: courier.daily_rate || 0.00
		};
		showCourierModal = true;
	}

	async function handleSaveCourier() {
		if (!courierForm.name || !courierForm.name.trim()) {
			onNotify("Por favor, preencha o nome do entregador.", "error");
			return;
		}

		isSaving = true;
		try {
			if (isEditing) {
				await updateCourier(courierForm.id, courierForm);
				onNotify(`Dados de ${courierForm.name} atualizados com sucesso!`, "success");
			} else {
				await createCourier(courierForm);
				onNotify(`${courierForm.name} cadastrado na roleta com sucesso!`, "success");
			}
			showCourierModal = false;
			await loadCouriers();
		} catch (err) {
			onNotify(err.message || "Erro ao salvar entregador.", "error");
		} finally {
			isSaving = false;
		}
	}

	async function handleDeleteCourier(courier) {
		if (!confirm(`Deseja realmente desativar o entregador ${courier.name}?`)) return;
		try {
			await deleteCourier(courier.id);
			onNotify(`${courier.name} desativado da equipe.`, "info");
			await loadCouriers();
		} catch (err) {
			onNotify(err.message || "Erro ao excluir entregador.", "error");
		}
	}

	async function handleToggleStatus(courier) {
		try {
			const updated = await toggleCourierStatus(courier.id);
			const label = updated.status === "available" ? "Disponível na Roleta" : "Pausa / Offline";
			onNotify(`${courier.name} agora está: ${label}`, "info");
			await loadCouriers();
		} catch (err) {
			onNotify(err.message || "Erro ao alternar status.", "error");
		}
	}

	async function handleReturnFromDelivery(courier) {
		try {
			await returnCourier(courier.id);
			onNotify(`Retorno registrado! ${courier.name} foi para o fim da fila da roleta.`, "success");
			await loadCouriers();
		} catch (err) {
			onNotify(err.message || "Erro ao registrar retorno.", "error");
		}
	}

	async function moveInQueue(courier, direction) {
		const available = couriers.filter(c => c.status === "available").sort((a, b) => a.queue_position - b.queue_position);
		const idx = available.findIndex(c => c.id === courier.id);
		if (idx === -1) return;

		const targetIdx = direction === "up" ? idx - 1 : idx + 1;
		if (targetIdx < 0 || targetIdx >= available.length) return;

		// Troca as posições
		const temp = available[idx];
		available[idx] = available[targetIdx];
		available[targetIdx] = temp;

		const orderedIds = available.map(c => c.id);
		try {
			await reorderCouriersQueue(orderedIds);
			await loadCouriers();
		} catch (err) {
			onNotify("Erro ao reordenar roleta.", "error");
		}
	}

	// Acerto Financeiro
	async function openSettlementTab(courier) {
		selectedCourierForSettlement = courier;
		activeViewTab = "settlement";
		await loadSettlementData();
	}

	async function loadSettlementData() {
		if (!selectedCourierForSettlement) return;
		isLoadingSettlement = true;
		settlementData = null;
		try {
			settlementData = await fetchCourierSettlement(selectedCourierForSettlement.id, settlementDate);
		} catch (err) {
			onNotify(err.message || "Erro ao calcular acerto financeiro.", "error");
		} finally {
			isLoadingSettlement = false;
		}
	}

	async function handleConfirmSettlement() {
		if (!settlementData) return;
		const sum = settlementData.summary;
		if (!confirm(`Confirmar o fechamento de acerto de ${selectedCourierForSettlement.name} para a data ${settlementDate}?\n${sum.actionRequired}`)) {
			return;
		}

		isSettling = true;
		try {
			await settleCourierAccount(selectedCourierForSettlement.id, {
				date: settlementDate,
				total_deliveries: sum.totalDeliveries,
				total_fees: sum.totalFees,
				daily_rate: sum.dailyRate,
				total_cash_collected: sum.totalCashCollected,
				net_balance: sum.netBalance,
				notes: settlementNotes
			});
			onNotify(`Fechamento de ${selectedCourierForSettlement.name} registrado com sucesso!`, "success");
			settlementNotes = "";
			await loadSettlementData();
		} catch (err) {
			onNotify(err.message || "Erro ao registrar fechamento.", "error");
		} finally {
			isSettling = false;
		}
	}

	async function loadHistory() {
		isLoadingHistory = true;
		try {
			settlementHistory = await fetchCourierSettlementHistory();
		} catch (err) {
			onNotify("Erro ao carregar histórico de fechamentos.", "error");
		} finally {
			isLoadingHistory = false;
		}
	}

	$: if (activeViewTab === "history") {
		loadHistory();
	}

	$: availableCouriers = couriers
		.filter(c => c.status === "available")
		.sort((a, b) => (Number(a.queue_position) || 999) - (Number(b.queue_position) || 999));
	$: nextCourier = availableCouriers.length > 0 ? availableCouriers[0] : null;

	function formatCurrency(val) {
		return Number(val || 0).toFixed(2).replace(".", ",");
	}

	function getCourierPortalUrl(courier) {
		const cleanPhone = (courier.phone || '').replace(/\D/g, '');
		const origin = typeof window !== 'undefined' ? window.location.origin : '';
		return `${origin}/motoboy?tel=${cleanPhone}`;
	}

	function copyCourierPortalLink(courier) {
		if (!courier.phone) {
			onNotify("Este entregador não possui telefone cadastrado.", "error");
			return;
		}
		const url = getCourierPortalUrl(courier);
		if (navigator.clipboard) {
			navigator.clipboard.writeText(url);
			onNotify(`Link do motoboy ${courier.name} copiado com sucesso!`, "success");
		} else {
			prompt("Copie o link do motoboy abaixo:", url);
		}
	}

	function sendCourierPortalWhatsApp(courier) {
		if (!courier.phone) {
			onNotify("Este entregador não possui telefone cadastrado.", "error");
			return;
		}
		const cleanPhone = courier.phone.replace(/\D/g, '');
		const intlPhone = (cleanPhone.length >= 12 && cleanPhone.startsWith('55')) ? cleanPhone : `55${cleanPhone}`;
		const url = getCourierPortalUrl(courier);
		const msg = encodeURIComponent(`Olá ${courier.name}! 🛵\nSegue o link do seu Portal de Entregas no celular com mapa, rotas e extrato do seu turno:\n\n${url}\n\nAbra no navegador do celular e adicione à tela inicial!`);
		window.open(`https://wa.me/${intlPhone}?text=${msg}`, '_blank');
	}
</script>

<div class="couriers-view-container">
	<!-- Top Bar / Cabeçalho Principal -->
	<header class="couriers-header">
		<div class="header-left">
			<div class="header-icon-badge">
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
					<circle cx="18.5" cy="17.5" r="3.5"></circle>
					<circle cx="5.5" cy="17.5" r="3.5"></circle>
					<circle cx="15" cy="5" r="1"></circle>
					<path d="M12 17.5V14l-3-3 4-3 2 3h2"></path>
				</svg>
			</div>
			<div class="header-titles">
				<div class="header-title-row">
					<h2>Entregadores & Roleta</h2>
					<span class="header-status-pill">
						<span class="pulse-dot green"></span>
						Fila Operacional
					</span>
				</div>
				<p>Gestão de equipe de motoboys, fila de despacho justo e fechamento financeiro de turnos</p>
			</div>
		</div>

		<div class="header-actions">
			<button
				type="button"
				class="btn-header-secondary {isLoading ? 'is-syncing' : ''}"
				on:click={loadCouriers}
				disabled={isLoading}
				title="Sincronizar entregadores e fila"
			>
				<Icon name="refresh" size={15} />
				<span>{isLoading ? 'Sincronizando...' : 'Atualizar'}</span>
			</button>

			<button type="button" class="btn-primary-action" on:click={openCreateModal}>
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
				<span>Novo Entregador</span>
			</button>
		</div>
	</header>

	<!-- Faixa de Resumo Operacional (Métricas Consolidadas) -->
	<div class="metrics-grid">
		<div class="metric-card">
			<div class="metric-header">
				<span class="metric-label">Na Fila (Disponíveis)</span>
				<span class="metric-badge-icon green"><Icon name="check" size={13} /></span>
			</div>
			<div class="metric-val-row">
				<span class="metric-number text-green">{availableCount}</span>
				<span class="pulse-dot green"></span>
			</div>
			<span class="metric-sub">Aguardando novo chamado na loja</span>
		</div>

		<div class="metric-card">
			<div class="metric-header">
				<span class="metric-label">Na Rua (Em Rota)</span>
				<span class="metric-badge-icon blue"><Icon name="motorcycle" size={13} /></span>
			</div>
			<div class="metric-val-row">
				<span class="metric-number text-blue">{busyCount}</span>
				<span class="pulse-dot blue"></span>
			</div>
			<span class="metric-sub">Levando pedidos aos clientes</span>
		</div>

		<div class="metric-card">
			<div class="metric-header">
				<span class="metric-label">Em Pausa / Offline</span>
				<span class="metric-badge-icon slate"><Icon name="pause" size={13} /></span>
			</div>
			<div class="metric-val-row">
				<span class="metric-number text-slate">{totalCouriers - (availableCount + busyCount)}</span>
			</div>
			<span class="metric-sub">Indisponíveis no momento</span>
		</div>

		<div class="metric-card highlight-metric">
			<div class="metric-header">
				<span class="metric-label">Viagens Concluídas Hoje</span>
				<span class="metric-badge-icon orange"><Icon name="award" size={13} /></span>
			</div>
			<div class="metric-val-row">
				<span class="metric-number text-orange">{totalDeliveriesToday}</span>
			</div>
			<span class="metric-sub">Entregas finalizadas pela equipe</span>
		</div>
	</div>

	<!-- Navegação por Abas -->
	<div class="tabs-nav-bar">
		<button
			type="button"
			class="tab-btn {activeViewTab === 'queue' ? 'active' : ''}"
			on:click={() => activeViewTab = 'queue'}
		>
			<span class="tab-icon"><Icon name="target" size={16} /></span>
			<span>Fila da Roleta</span>
			{#if availableCount > 0}
				<span class="tab-count-pill">{availableCount}</span>
			{/if}
		</button>

		<button
			type="button"
			class="tab-btn {activeViewTab === 'team' ? 'active' : ''}"
			on:click={() => activeViewTab = 'team'}
		>
			<span class="tab-icon"><Icon name="users" size={16} /></span>
			<span>Equipe & Cadastro</span>
			<span class="tab-count-pill">{totalCouriers}</span>
		</button>

		<button
			type="button"
			class="tab-btn {activeViewTab === 'settlement' ? 'active' : ''}"
			on:click={() => activeViewTab = 'settlement'}
		>
			<span class="tab-icon"><Icon name="wallet" size={16} /></span>
			<span>Fechamento & Acerto</span>
		</button>

		<button
			type="button"
			class="tab-btn {activeViewTab === 'history' ? 'active' : ''}"
			on:click={() => activeViewTab = 'history'}
		>
			<span class="tab-icon"><Icon name="file-text" size={16} /></span>
			<span>Histórico</span>
		</button>
	</div>

	<!-- Conteúdo Dinâmico por Aba -->
	<div class="couriers-view-content">
		{#if errorMsg}
			<div class="error-banner">
				<div class="error-content">
					<Icon name="alert" size={16} />
					<span>{errorMsg}</span>
				</div>
				<button type="button" class="btn-error-retry" on:click={loadCouriers}>Tentar Novamente</button>
			</div>
		{/if}

		{#if isLoading && couriers.length === 0}
			<div class="skeleton-grid">
				<div class="skeleton-banner"></div>
				<div class="skeleton-cards">
					<div class="skeleton-card"></div>
					<div class="skeleton-card"></div>
					<div class="skeleton-card"></div>
				</div>
			</div>
		{:else if activeViewTab === 'queue'}
			<CouriersQueueTab
				{couriers}
				{nextCourier}
				{formatCurrency}
				onOpenCreateModal={openCreateModal}
				onOpenSettlementTab={openSettlementTab}
				onCopyPortalLink={copyCourierPortalLink}
				onSendPortalWhatsApp={sendCourierPortalWhatsApp}
				onReturnFromDelivery={handleReturnFromDelivery}
				onMoveInQueue={moveInQueue}
				onToggleStatus={handleToggleStatus}
			/>
		{:else if activeViewTab === 'team'}
			<CouriersTeamTab
				{couriers}
				{formatCurrency}
				onOpenCreateModal={openCreateModal}
				onOpenEditModal={openEditModal}
				onDeleteCourier={handleDeleteCourier}
				onCopyPortalLink={copyCourierPortalLink}
				onSendPortalWhatsApp={sendCourierPortalWhatsApp}
			/>
		{:else if activeViewTab === 'settlement'}
			<CouriersSettlementTab
				{couriers}
				bind:selectedCourierForSettlement
				bind:settlementDate
				{settlementData}
				{isLoadingSettlement}
				bind:settlementNotes
				{isSettling}
				{formatCurrency}
				onSelectCourier={loadSettlementData}
				onChangeDate={loadSettlementData}
				onReloadSettlement={loadSettlementData}
				onConfirmSettlement={handleConfirmSettlement}
			/>
		{:else if activeViewTab === 'history'}
			<CouriersHistoryTab
				{settlementHistory}
				{isLoadingHistory}
				{formatCurrency}
				onReloadHistory={loadHistory}
			/>
		{/if}
	</div>

	<!-- Modal de Cadastro / Edição -->
	<CourierModal
		show={showCourierModal}
		{isEditing}
		bind:courierForm
		{isSaving}
		onClose={() => showCourierModal = false}
		onSave={handleSaveCourier}
	/>
</div>

<style>
	.couriers-view-container {
		display: flex;
		flex-direction: column;
		min-height: 100%;
		background: var(--neutral-bg, #F8FAFC);
		overflow-y: auto;
		padding: 24px;
		box-sizing: border-box;
		font-family: var(--font-sans, 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif);
		color: var(--text-primary, #0F172A);
	}

	/* Top Header */
	.couriers-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 24px;
		flex-wrap: wrap;
		gap: 16px;
	}

	.header-left {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	.header-icon-badge {
		width: 48px;
		height: 48px;
		border-radius: var(--radius-md, 12px);
		background: linear-gradient(135deg, #FFF7ED, #FFEDD5);
		color: var(--brand-primary, #FF6A00);
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 2px 8px rgba(255, 106, 0, 0.16);
		flex-shrink: 0;
	}

	.header-titles {
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	.header-title-row {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.couriers-header h2 {
		font-size: 22px;
		font-weight: 800;
		color: var(--text-primary, #0F172A);
		margin: 0;
		letter-spacing: -0.3px;
	}

	.header-status-pill {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: var(--semantic-success-soft, #ECFDF5);
		border: 1px solid #A7F3D0;
		color: var(--semantic-success, #059669);
		font-size: 11px;
		font-weight: 700;
		padding: 3px 9px;
		border-radius: var(--radius-full, 9999px);
		text-transform: uppercase;
		letter-spacing: 0.4px;
	}

	.couriers-header p {
		margin: 0;
		font-size: 13.5px;
		color: var(--text-secondary, #475569);
	}

	.header-actions {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.btn-header-secondary {
		background: var(--neutral-surface, #FFFFFF);
		color: var(--text-secondary, #475569);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-md, 10px);
		padding: 10px 16px;
		font-size: 13.5px;
		font-weight: 600;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		transition: all var(--transition-fast, 0.15s ease);
		box-shadow: var(--shadow-sm, 0 1px 2px rgba(15, 23, 42, 0.05));
	}

	.btn-header-secondary:hover:not(:disabled) {
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-primary, #0F172A);
		border-color: var(--neutral-border-focus, #94A3B8);
	}

	.btn-header-secondary.is-syncing :global(svg) {
		animation: spin 1s linear infinite;
	}

	.btn-primary-action {
		background: var(--brand-primary, #FF6A00);
		color: #FFFFFF;
		border: none;
		border-radius: var(--radius-md, 10px);
		padding: 10px 18px;
		font-size: 13.5px;
		font-weight: 700;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		transition: all var(--transition-fast, 0.15s ease);
		box-shadow: 0 4px 12px rgba(255, 106, 0, 0.25);
	}

	.btn-primary-action:hover {
		background: var(--brand-primary-hover, #E85A00);
		transform: translateY(-1px);
		box-shadow: 0 6px 16px rgba(255, 106, 0, 0.32);
	}

	/* Resumo Operacional */
	.metrics-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 16px;
		margin-bottom: 24px;
	}

	.metric-card {
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-lg, 16px);
		padding: 16px 18px;
		display: flex;
		flex-direction: column;
		gap: 6px;
		box-shadow: var(--shadow-card, 0 1px 3px rgba(15, 23, 42, 0.06));
		transition: transform var(--transition-fast, 0.15s ease), box-shadow var(--transition-fast, 0.15s ease);
	}

	.metric-card:hover {
		transform: translateY(-1px);
		box-shadow: var(--shadow-card-hover, 0 4px 6px rgba(15, 23, 42, 0.08));
	}

	.metric-card.highlight-metric {
		background: linear-gradient(135deg, #FFFDFB, #FFF7ED);
		border-color: #FED7AA;
	}

	.metric-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.metric-label {
		font-size: 11.5px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		color: var(--text-secondary, #475569);
	}

	.metric-badge-icon {
		width: 24px;
		height: 24px;
		border-radius: 6px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.metric-badge-icon.green { background: #ECFDF5; color: #059669; }
	.metric-badge-icon.blue { background: #EFF6FF; color: #2563EB; }
	.metric-badge-icon.slate { background: #F1F5F9; color: #64748B; }
	.metric-badge-icon.orange { background: #FFF7ED; color: #FF6A00; }

	.metric-val-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 2px;
	}

	.metric-number {
		font-size: 26px;
		font-weight: 900;
		line-height: 1.1;
	}

	.text-green { color: var(--semantic-success, #059669); }
	.text-blue { color: var(--semantic-info, #2563EB); }
	.text-orange { color: var(--brand-primary, #FF6A00); }
	.text-slate { color: var(--text-secondary, #475569); }

	.metric-sub {
		font-size: 12px;
		color: var(--text-muted, #94A3B8);
	}

	.pulse-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		display: inline-block;
	}

	.pulse-dot.green {
		background: #10B981;
		box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
		animation: pulseGreen 2s infinite ease-in-out;
	}

	.pulse-dot.blue {
		background: #2563EB;
		box-shadow: 0 0 8px rgba(37, 99, 235, 0.6);
		animation: pulseBlue 2s infinite ease-in-out;
	}

	@keyframes pulseGreen {
		0%, 100% { opacity: 1; transform: scale(1); }
		50% { opacity: 0.6; transform: scale(1.15); }
	}

	@keyframes pulseBlue {
		0%, 100% { opacity: 1; transform: scale(1); }
		50% { opacity: 0.6; transform: scale(1.15); }
	}

	/* Navegação por Abas */
	.tabs-nav-bar {
		display: flex;
		gap: 6px;
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		margin-bottom: 24px;
		overflow-x: auto;
		scrollbar-width: none;
	}

	.tabs-nav-bar::-webkit-scrollbar {
		display: none;
	}

	.tab-btn {
		background: transparent;
		border: none;
		border-bottom: 2.5px solid transparent;
		padding: 12px 18px;
		font-size: 13.5px;
		font-weight: 700;
		color: var(--text-secondary, #475569);
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		transition: all var(--transition-fast, 0.15s ease);
		white-space: nowrap;
	}

	.tab-btn:hover {
		color: var(--text-primary, #0F172A);
	}

	.tab-btn.active {
		color: var(--brand-primary, #FF6A00);
		border-bottom-color: var(--brand-primary, #FF6A00);
	}

	.tab-count-pill {
		font-size: 11px;
		font-weight: 800;
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-secondary, #475569);
		padding: 2px 7px;
		border-radius: var(--radius-full, 9999px);
	}

	.tab-btn.active .tab-count-pill {
		background: var(--brand-primary-soft, #FFF7ED);
		color: var(--brand-primary, #FF6A00);
	}

	/* Banner de Erro */
	.error-banner {
		background: var(--semantic-danger-soft, #FEF2F2);
		border: 1px solid #FECDD3;
		color: var(--semantic-danger, #DC2626);
		border-radius: var(--radius-md, 10px);
		padding: 12px 18px;
		margin-bottom: 20px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 12px;
		font-size: 13.5px;
		font-weight: 600;
	}

	.error-content {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.btn-error-retry {
		background: #FFFFFF;
		border: 1px solid #FECDD3;
		color: var(--semantic-danger, #DC2626);
		padding: 6px 12px;
		border-radius: 6px;
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
	}

	/* Loading States & Skeletons */
	.skeleton-grid {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.skeleton-banner {
		height: 100px;
		background: linear-gradient(90deg, #E2E8F0 25%, #F1F5F9 50%, #E2E8F0 75%);
		background-size: 200% 100%;
		animation: shimmer 1.5s infinite;
		border-radius: var(--radius-lg, 16px);
	}

	.skeleton-cards {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 16px;
	}

	.skeleton-card {
		height: 200px;
		background: linear-gradient(90deg, #E2E8F0 25%, #F1F5F9 50%, #E2E8F0 75%);
		background-size: 200% 100%;
		animation: shimmer 1.5s infinite;
		border-radius: var(--radius-lg, 16px);
	}

	@keyframes spin {
		to { transform: rotate(360deg); }
	}

	@keyframes shimmer {
		0% { background-position: 200% 0; }
		100% { background-position: -200% 0; }
	}

	/* Responsividade */
	@media (max-width: 1024px) {
		.metrics-grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media (max-width: 768px) {
		.couriers-view-container {
			padding: 16px;
		}

		.couriers-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 14px;
		}

		.header-actions {
			width: 100%;
		}

		.btn-header-secondary {
			flex: 1;
			justify-content: center;
		}

		.btn-primary-action {
			flex: 2;
			justify-content: center;
		}
	}

	@media (max-width: 599px) {
		.metrics-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
